/**
 * components/ChatChartPanel.js
 *
 * Pure chart renderer for chatbot assistant messages.
 * Supports three chart types:
 *   • line  — time-series trend; renders two series (solid "Actual" + dashed "Projected")
 *             when the data contains both `actual` and `projected` columns (forecast mode)
 *   • bar   — grouped category comparison
 *   • pie   — small distribution (≤ 6 slices)
 *
 * Props:
 *   chartType  string         "line" | "bar" | "pie"
 *   chartData  string         JSON array of result-row objects (from the WebSocket done packet)
 *   height     number|string  ResponsiveContainer height; defaults to 185
 *
 * Download logic lives in ChartDialog, not here.
 */
import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RCTooltip, Legend,
  ResponsiveContainer,
} from 'recharts';

// ── Colour palette (matches the dashboard pages) ──────────────────────────────

const SERIES_COLORS = ['#1565C0', '#00897B', '#F57F17', '#AD1457', '#6A1B9A'];
const PIE_COLORS    = ['#1565C0', '#00897B', '#F57F17', '#AD1457', '#6A1B9A', '#0277BD'];

// Labels that identify the x-axis (time dimension)
const TIME_LABELS = new Set(['month_label', 'day_label']);

// ── Axis-key derivation ───────────────────────────────────────────────────────

/**
 * Derives xKey and seriesKeys from the first data row's column names.
 *
 * Forecast mode is detected when BOTH `actual` and `projected` columns are present —
 * this is the enriched format produced by the backend ForecastService.
 *
 * Returns { xKey, seriesKeys, hasForecast } for line/bar, or { nameKey, valueKey } for pie.
 */
function deriveKeys(rows, chartType) {
  if (!rows?.length) return null;
  const cols = Object.keys(rows[0]);

  if (chartType === 'pie') {
    const [nameKey, valueKey] = cols;
    return { nameKey, valueKey };
  }

  const xKey = cols.find((c) => TIME_LABELS.has(c))
            ?? cols.find((c) => typeof rows[0][c] === 'string')
            ?? cols[0];

  // Forecast mode: ForecastService emits {month_label, actual, projected[, projected_custom][, budget]}
  const hasForecast   = cols.includes('actual') && cols.includes('projected');
  const hasCustomRate = cols.includes('projected_custom');
  const hasBudget     = cols.includes('budget');
  const seriesKeys    = cols.filter((c) => c !== xKey);

  return { xKey, seriesKeys, hasForecast, hasCustomRate, hasBudget };
}

// ── Common chart props ────────────────────────────────────────────────────────

const CHART_MARGIN  = { top: 5, right: 10, left: 10, bottom: 0 };
const AXIS_TICK     = { fontSize: 12 };

function fmtYTick(v) {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(0)}K`;
  return v;
}

function makeYTickFormatter(yAxisLabel) {
  if (yAxisLabel === '%') return (v) => `${v}%`;
  return fmtYTick;
}

function makeYAxisLabel(yAxisLabel) {
  if (!yAxisLabel || yAxisLabel === '%') return undefined;
  return { value: yAxisLabel, angle: -90, position: 'insideLeft',
           style: { textAnchor: 'middle', fontSize: 10, fill: '#9e9e9e' } };
}

const GRID_PROPS    = { strokeDasharray: '3 3', stroke: '#f0f0f0' };
const BAR_RADIUS    = [4, 4, 0, 0];

// ── Sub-renderers ─────────────────────────────────────────────────────────────

function LineChartView({ data, xKey, seriesKeys, hasForecast, hasCustomRate, hasBudget, height, yAxisLabel }) {
  const tickFmt  = makeYTickFormatter(yAxisLabel);
  const axisLbl  = makeYAxisLabel(yAxisLabel);
  const axisW    = axisLbl ? 65 : 55;

  // Forecast mode: data has {month_label, actual, projected[, projected_custom][, budget]}
  if (hasForecast) {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey={xKey} tick={AXIS_TICK} />
          <YAxis tick={AXIS_TICK} width={axisW} tickFormatter={tickFmt} label={axisLbl} />
          <RCTooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {/* Historical values — blue solid */}
          <Line type="monotone" dataKey="actual" name="Actual"
                stroke="#1565C0" strokeWidth={2} dot={{ r: 3 }} connectNulls />
          {/* Linear regression projection — blue dashed */}
          <Line type="monotone" dataKey="projected" name="Projected (trend)"
                stroke="#1565C0" strokeWidth={1.5} strokeDasharray="5 3" dot={{ r: 2 }} connectNulls />
          {/* User-specified custom rate projection — red dashed (scenario b) */}
          {hasCustomRate && (
            <Line type="monotone" dataKey="projected_custom" name="Projected (custom rate)"
                  stroke="#EF5350" strokeWidth={1.5} strokeDasharray="5 3" dot={{ r: 2 }} connectNulls />
          )}
          {/* Budget reference line — orange solid (scenario c) */}
          {hasBudget && (
            <Line type="monotone" dataKey="budget" name="Budget"
                  stroke="#F57F17" strokeWidth={1.5} dot={false} connectNulls />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // Regular line chart: render each numeric series key
  const displayKeys = seriesKeys.filter((k) => k !== 'actual' && k !== 'projected' && k !== 'projected_custom' && k !== 'budget');
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} />
        <YAxis tick={AXIS_TICK} width={axisW} tickFormatter={tickFmt} label={axisLbl} />
        <RCTooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {displayKeys.map((key, i) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function BarChartView({ data, xKey, seriesKeys, height, yAxisLabel }) {
  const tickFmt = makeYTickFormatter(yAxisLabel);
  const axisLbl = makeYAxisLabel(yAxisLabel);
  const axisW   = axisLbl ? 65 : 55;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey={xKey} tick={AXIS_TICK} />
        <YAxis tick={AXIS_TICK} width={axisW} tickFormatter={tickFmt} label={axisLbl} />
        <RCTooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {seriesKeys.map((key, i) => (
          <Bar
            key={key}
            dataKey={key}
            fill={SERIES_COLORS[i % SERIES_COLORS.length]}
            radius={BAR_RADIUS}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieChartView({ data, nameKey, valueKey, height }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={70}
          paddingAngle={3}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
          ))}
        </Pie>
        <RCTooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ChatChartPanel({ chartType, chartData, height = 185, yAxisLabel = null }) {
  const rows = useMemo(() => {
    if (!chartData) return null;
    try {
      const parsed = typeof chartData === 'string' ? JSON.parse(chartData) : chartData;
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch {
      return null;
    }
  }, [chartData]);

  const keys = useMemo(() => deriveKeys(rows, chartType), [rows, chartType]);

  if (!rows || !keys || !chartType) return null;

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return <LineChartView data={rows} xKey={keys.xKey} seriesKeys={keys.seriesKeys} hasForecast={keys.hasForecast} hasCustomRate={keys.hasCustomRate} hasBudget={keys.hasBudget} height={height} yAxisLabel={yAxisLabel} />;
      case 'bar':
        return <BarChartView data={rows} xKey={keys.xKey} seriesKeys={keys.seriesKeys} height={height} yAxisLabel={yAxisLabel} />;
      case 'pie':
        return <PieChartView data={rows} nameKey={keys.nameKey} valueKey={keys.valueKey} height={height} />;
      default:
        return null;
    }
  };

  const chart = renderChart();
  if (!chart) return null;

  return (
    <Box
      sx={{
        width: '100%',
        height: height === '100%' ? '100%' : undefined,
        flex: height === '100%' ? 1 : undefined,        
      }}
    >
      {chart}
    </Box>
  );
}
